import { useCallback, useMemo } from 'react';
import { ListRenderItemInfo } from 'react-native';

import { Carousel } from '@fhs/ui';

import {
  ChallengeCard,
  ChallengeCardProps,
  ChallengeCardSkeleton,
  useChallengeCardWidth,
} from '../../components';
import { useOnPressChallenge } from '../../hooks/use-on-press-challenge';
import { useChallengeDetailsModalStore } from '../../modals';
import { useChallengesUi } from '../../queries/loyalty.queries';
import { getChallengeBadge } from '../../utils';
import { getChallengeButtonText } from '../../utils';
import { getChallengeDescription } from '../../utils';

const renderChallengeCard = ({ item }: ListRenderItemInfo<ChallengeCardProps>) => (
  <ChallengeCard {...item} />
);

export function Challenges({ isLoading }: { isLoading: boolean }) {
  const { data } = useChallengesUi();
  const { show } = useChallengeDetailsModalStore();
  const onPressChallenge = useOnPressChallenge();
  const itemWidth = useChallengeCardWidth();

  const challengesCardProps = useMemo(
    () =>
      data?.map<ChallengeCardProps>(challenge => ({
        image: challenge.image,
        title: challenge.name ?? '',
        description: getChallengeDescription(challenge),
        onPress: async () => await onPressChallenge(challenge),
        onPressInfo: () => show(challenge.id),
        buttonText: getChallengeButtonText(challenge.status),
        progress: challenge.progress,
        badge: getChallengeBadge(challenge.status, challenge.progress),
      })) ?? [],
    [data, onPressChallenge, show]
  );

  const renderCarouselLoading = useCallback(() => {
    return <ChallengeCardSkeleton style={{ alignSelf: 'center', marginTop: 16 }} />;
  }, []);

  if (isLoading) {
    return renderCarouselLoading();
  }

  return (
    <Carousel
      renderCarouselLoading={renderCarouselLoading}
      style={{ marginTop: 16 }}
      enableNativeSnap
      data={challengesCardProps}
      itemWidth={itemWidth}
      renderItem={renderChallengeCard}
      scrollEnabled={challengesCardProps.length > 1}
    />
  );
}
